import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  createStyles,
  Typography,
} from '@material-ui/core';
import { useIsMobile } from '../../../../util/style-utils';
import { makeStyles } from '@material-ui/core/styles';
import theme from '../../../../themes/theme';
import colors from '../../../../themes/main-colors';
import SlugLink from '../../../slug-link';
import Stepper from './Stepper';

const useStyles = (
  isMobile?: boolean,
  PaneBackgroundColor?: string,
  imageFocusHorizontal?: string,
  imageFocusVertical?: string,
) =>
  makeStyles(() => {
    const heading = {
      letterSpacing: '0.720px',
      fontFamily: 'Forma-DJR-Display',
      marginBottom: theme.spacing(1),
    };

    return createStyles({
      cardRoot: {
        boxShadow: 'none',
        borderRadius: 'unset',
      },
      cardContent: {
        padding: 0,
        '&:last-child': {
          padding: 0,
        },
      },
      carouselContainer: {
        display: 'flex',
        [theme.breakpoints.up('sm')]: {
          width: '100%',
          height: '400px',
        },
        [theme.breakpoints.down('sm')]: {
          minHeight: '253px',
        },
      },
      panesContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        [theme.breakpoints.up('sm')]: {
          height: '400px',
        },
        [theme.breakpoints.down('sm')]: {
          minHeight: '253px',
        },
      },
      pane: {
        backgroundColor: getBackgroundColor(PaneBackgroundColor),
        height: '100%',
        padding: '20px',
        display: 'flex',
        justifyContent: 'center',
        [theme.breakpoints.down('sm')]: {
          flexDirection: 'column',
        },
      },
      paneImgContainer: {
        display: 'flex',
        alignItems: 'center',
      },
      paneImg: {
        objectFit: `${
          imageFocusHorizontal || imageFocusVertical ? 'cover' : 'fill'
        }`,
        objectPosition: `${imageFocusHorizontal ||
          '50%'} ${imageFocusVertical || '50%'}`,
        width: '561px',
        height: '350px',
        border: `10px solid ${colors.white}`,
      },
      paneDetailsContainer: {
        position: 'relative',
        width: '100%',
        padding: '0 20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        [theme.breakpoints.down('sm')]: {
          marginLeft: 'unset',
          height: 'inherit',
          padding: '0 10px',
        },
      },
      paneHeader: {
        ...heading,
        fontWeight: 'bold',
        fontSize: theme.spacing(1.75),
        lineHeight: theme.typography.pxToRem(28),
        color: colors.noirBlur,
      },
      paneSubHeader: {
        ...heading,
        lineHeight: theme.typography.pxToRem(43.2),
        color: colors.sparkBlue,
        fontWeight: 'bold',
        fontSize: theme.spacing(4.5),
      },
      leftArrow: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        height: isMobile ? '' : '400px',
        [theme.breakpoints.down('sm')]: {
          marginLeft: 'auto',
        },
      },
      rightArrow: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        height: isMobile ? '' : '400px',
        [theme.breakpoints.down('sm')]: {
          marginRight: 'auto',
        },
      },
    });
  });

export const getBackgroundColor = (color: any | null): any | null => {
  let bgColor = colors.white;
  switch (color) {
    case 'Blue':
      bgColor = colors.riverBlue;
      break;
    case 'Grey':
      bgColor = colors.cloudGrey2;
      break;
    case 'Linen-White':
      bgColor = colors.linenWhite;
      break;
    case 'Yellow':
      bgColor = colors.lightYellow;
      break;
    default:
      bgColor = colors.white;
      break;
  }
  return bgColor;
};

type TCarousel = {
  panes: {
    header: string;
    subHeader: string;
    slug: string;
    articlePublishDate: string;
    image: string;
    backgroundColor?: string;
    imageFocusHorizontal?: string;
    imageFocusVertical?: string;
  }[];
  carouselAutoPlay?: boolean;
  carouselAutoPlayDelay?: number;
};

const Carousel = (props: TCarousel) => {
  const { panes, carouselAutoPlay, carouselAutoPlayDelay = 3 } = props;
  const finalCarouselAutoPlayDelay = carouselAutoPlayDelay ?? 3;
  const [activeStep, setActiveStep] = useState(0);
  const isMobile = useIsMobile();
  const classes = useStyles(
    isMobile,
    panes[activeStep]?.backgroundColor,
    panes[activeStep]?.imageFocusHorizontal,
    panes[activeStep]?.imageFocusVertical,
  )();

  const autoPlayDelay = finalCarouselAutoPlayDelay * 1000;
  const numberOfPanes = panes?.length;
  useEffect(() => {
    if (carouselAutoPlay && numberOfPanes > 1) {
      const interval = setInterval(() => {
        setActiveStep(prevStep =>
          prevStep === numberOfPanes - 1 ? 0 : prevStep + 1,
        );
      }, autoPlayDelay);

      return () => clearInterval(interval);
    }
  }, [carouselAutoPlay, numberOfPanes, autoPlayDelay]);

  return (
    <>
      <Card className={classes.cardRoot}>
        {isMobile && (
          <CardMedia
            component="img"
            image={panes[activeStep]?.image}
            height={400}
          />
        )}
        <CardContent className={classes.cardContent}>
          <Box className={classes.carouselContainer}>
            <Box className={classes.panesContainer}>
              <Box className={classes.pane}>
                <Container
                  maxWidth="lg"
                  style={{ paddingRight: 0, paddingLeft: 0, display: 'flex' }}
                >
                  <Box className={classes.paneDetailsContainer}>
                    {panes[activeStep]?.header && (
                      <Typography variant={'h1'} className={classes.paneHeader}>
                        {panes[activeStep]?.header}
                      </Typography>
                    )}
                    {panes[activeStep]?.subHeader && (
                      <SlugLink
                        slug={panes[activeStep]?.slug}
                        className={classes.paneSubHeader}
                        linkText={panes[activeStep]?.subHeader}
                      />
                    )}
                    {panes[activeStep]?.articlePublishDate && (
                      <Typography
                        variant={'body1'}
                        className={classes.paneHeader}
                      >
                        {panes[activeStep]?.articlePublishDate}
                      </Typography>
                    )}

                    {!isMobile && (panes?.length ?? 0) > 1 && (
                      <Stepper
                        steps={panes?.length || 1}
                        activeStep={activeStep}
                        setActiveStep={setActiveStep}
                      />
                    )}
                  </Box>
                  {!isMobile && panes[activeStep]?.image && (
                    <Box className={classes.paneImgContainer}>
                      <img
                        alt=""
                        src={panes[activeStep]?.image}
                        className={classes.paneImg}
                      />
                    </Box>
                  )}
                </Container>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {isMobile && (panes?.length ?? 0) > 1 && (
        <Stepper
          steps={panes?.length || 1}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
        />
      )}
    </>
  );
};

export default Carousel;
